const SKIP_TAGS = new Set([
  'SCRIPT', 'NOSCRIPT', 'STYLE', 'PRE', 'CODE',
  'SVG', 'PATH', 'IFRAME', 'EMBED', 'OBJECT', 'CANVAS',
  'VIDEO', 'AUDIO',
]);

/** 这些标签的文本内容不翻译，但 placeholder/title 仍可译 */
const SKIP_TEXT_TAGS = new Set([
  'TEXTAREA', 'INPUT', 'SELECT', 'OPTION',
]);

const ATTR_NAMES = ['placeholder', 'title', 'aria-label', 'alt'];

const CACHE_KEY = 'niuTransTextCache_v1';
const CACHE_MAX = 8000;

const translatedToSource = new Map();
const sourceToTranslated = new Map();
let cacheLoaded = false;

export function clearTranslationMaps() {
  translatedToSource.clear();
  sourceToTranslated.clear();
}

export function getCachedTranslation(source) {
  return sourceToTranslated.get(source);
}

export function setCachedTranslation(source, translated) {
  if (!source || !translated || source === translated) return;
  sourceToTranslated.set(source, translated);
  translatedToSource.set(translated, source);
}

export function loadPersistedCache() {
  if (cacheLoaded) return sourceToTranslated.size;
  cacheLoaded = true;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return 0;
    const obj = JSON.parse(raw);
    Object.keys(obj || {}).forEach(function (k) {
      const v = obj[k];
      if (typeof v === 'string' && v && v !== k) {
        sourceToTranslated.set(k, v);
        translatedToSource.set(v, k);
      }
    });
  } catch (e) { /* ignore */ }
  return sourceToTranslated.size;
}

export function persistCache() {
  try {
    const obj = {};
    let i = 0;
    sourceToTranslated.forEach(function (v, k) {
      if (i >= CACHE_MAX) return;
      obj[k] = v;
      i += 1;
    });
    localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch (e) { /* quota ignore */ }
}

function hasCjk(text) {
  if (!text) return false;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (
      (code >= 0x4e00 && code <= 0x9fff) ||
      (code >= 0x3400 && code <= 0x4dbf) ||
      (code >= 0xf900 && code <= 0xfaff)
    ) {
      return true;
    }
  }
  return false;
}

function shouldSkipTextNode(node) {
  if (!node || node.nodeType !== 3) return true;
  const parent = node.parentElement || node.parentNode;
  if (!parent || !parent.nodeName) return true;
  if (SKIP_TAGS.has(parent.nodeName) || SKIP_TEXT_TAGS.has(parent.nodeName)) return true;
  if (parent.isContentEditable) return true;
  if (typeof parent.closest === 'function') {
    // 不碰插件遮罩、消息层，以及 PDF/iframe 相关区域（避免影响业务页）
    if (parent.closest('[data-niu-skip="1"], .ant-message, .ant-notification, .niu-trans-mask, iframe, embed, object, .pdf_conent, .pdfjs')) {
      return true;
    }
  }
  const text = (node.data || '').trim();
  if (!text || text.length >= 5120) return true;
  if ((text[0] === '{' || text[0] === '[') && text.length > 1) {
    try {
      JSON.parse(text);
      return true;
    } catch (e) { /* not json */ }
  }
  return false;
}

export function isNearViewport(node) {
  try {
    const el = node.nodeType === 1 ? node : node.parentElement;
    if (!el || typeof el.getBoundingClientRect !== 'function') return true;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || 800;
    return rect.top < vh * 1.35 && rect.bottom > -80;
  } catch (e) {
    return true;
  }
}

function shouldSkipElement(el) {
  if (!el || el.nodeType !== 1) return true;
  if (SKIP_TAGS.has(el.nodeName)) return true;
  if (typeof el.closest === 'function') {
    if (el.closest('[data-niu-skip="1"], .ant-message, .ant-notification, .niu-trans-mask, iframe, embed, object, .pdf_conent, .pdfjs')) {
      return true;
    }
  }
  return false;
}

/**
 * 收集需要翻译的属性（placeholder / title / aria-label / alt）
 * 返回 { attrs: [{ el, attr, text }], texts: string[] }
 */
export function collectAttrTargets(root, opts) {
  const onlyCjk = !opts || opts.onlyCjk !== false;
  const mount = root || document.getElementById('app') || document.body;
  const attrs = [];
  const texts = [];
  if (!mount || typeof mount.querySelectorAll !== 'function') {
    return { attrs: attrs, texts: texts };
  }

  const selector = ATTR_NAMES.map(function (a) { return '[' + a + ']'; }).join(',');
  let list = [];
  try {
    list = mount.querySelectorAll(selector);
  } catch (e) {
    return { attrs: attrs, texts: texts };
  }

  for (let i = 0; i < list.length; i++) {
    const el = list[i];
    if (shouldSkipElement(el)) continue;
    for (let j = 0; j < ATTR_NAMES.length; j++) {
      const attr = ATTR_NAMES[j];
      if (!el.hasAttribute(attr)) continue;
      const text = String(el.getAttribute(attr) || '').trim();
      if (!text || text.length >= 5120) continue;
      if (onlyCjk && !hasCjk(text)) continue;
      if (translatedToSource.has(text)) continue;
      attrs.push({ el: el, attr: attr, text: text });
      texts.push(text);
    }
  }
  return { attrs: attrs, texts: texts };
}

export function collectTextNodes(root, opts) {
  const onlyCjk = !opts || opts.onlyCjk !== false;
  const mount = root || document.getElementById('app') || document.body;
  const nodes = [];
  const texts = [];
  if (!mount) return { nodes: nodes, texts: texts };

  if (typeof document.createTreeWalker === 'function') {
    const walker = document.createTreeWalker(mount, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (shouldSkipTextNode(node)) return NodeFilter.FILTER_REJECT;
        const text = (node.data || '').trim();
        if (onlyCjk && !hasCjk(text)) return NodeFilter.FILTER_REJECT;
        if (translatedToSource.has(text)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    let current = walker.nextNode();
    while (current) {
      nodes.push(current);
      texts.push((current.data || '').trim());
      current = walker.nextNode();
    }
    return { nodes: nodes, texts: texts };
  }

  function walk(node) {
    if (!node) return;
    if (node.nodeType === 1 && SKIP_TAGS.has(node.nodeName)) return;
    if (node.nodeType === 3) {
      if (shouldSkipTextNode(node)) return;
      const text = (node.data || '').trim();
      if (onlyCjk && !hasCjk(text)) return;
      if (translatedToSource.has(text)) return;
      nodes.push(node);
      texts.push(text);
      return;
    }
    let child = node.firstChild;
    while (child) {
      walk(child);
      child = child.nextSibling;
    }
  }
  walk(mount);
  return { nodes: nodes, texts: texts };
}

/**
 * 合并文本节点 + 属性节点，供统一翻译
 * items: [{ type:'text', node, text } | { type:'attr', el, attr, text }]
 */
export function collectAllTargets(root, opts) {
  const textPart = collectTextNodes(root, opts);
  const attrPart = collectAttrTargets(root, opts);
  const items = [];
  const texts = [];

  for (let i = 0; i < textPart.nodes.length; i++) {
    items.push({ type: 'text', node: textPart.nodes[i], text: textPart.texts[i] });
    texts.push(textPart.texts[i]);
  }
  for (let j = 0; j < attrPart.attrs.length; j++) {
    const a = attrPart.attrs[j];
    items.push({ type: 'attr', el: a.el, attr: a.attr, text: a.text });
    texts.push(a.text);
  }
  return { items: items, texts: texts, nodes: textPart.nodes };
}

export function applyTranslations(nodes, sourceTexts, translatedTexts) {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const source = sourceTexts[i];
    let translated = translatedTexts[i];
    if (!node || !source || translated == null || translated === '') continue;
    if (typeof translated !== 'string') translated = String(translated);
    if (translated === source) continue;
    const full = node.data || '';
    if (full.trim() !== source) continue;
    const lead = full.match(/^\s*/)[0];
    const trail = full.match(/\s*$/)[0];
    node.data = lead + translated + trail;
    setCachedTranslation(source, translated);
  }
}

export function applyAttrTranslations(attrs, sourceTexts, translatedTexts) {
  for (let i = 0; i < attrs.length; i++) {
    const item = attrs[i];
    const source = sourceTexts[i];
    let translated = translatedTexts[i];
    if (!item || !item.el || !source || translated == null || translated === '') continue;
    if (typeof translated !== 'string') translated = String(translated);
    if (translated === source) continue;
    const current = String(item.el.getAttribute(item.attr) || '').trim();
    if (current !== source) continue;
    item.el.setAttribute(item.attr, translated);
    setCachedTranslation(source, translated);
  }
}

export function applyTargetTranslations(items, translatedTexts) {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const source = item && item.text;
    let translated = translatedTexts[i];
    if (!item || !source || translated == null || translated === '') continue;
    if (typeof translated !== 'string') translated = String(translated);
    if (translated === source) continue;

    if (item.type === 'text') {
      const node = item.node;
      if (!node) continue;
      const full = node.data || '';
      if (full.trim() !== source) continue;
      const lead = full.match(/^\s*/)[0];
      const trail = full.match(/\s*$/)[0];
      node.data = lead + translated + trail;
      setCachedTranslation(source, translated);
    } else if (item.type === 'attr') {
      if (!item.el) continue;
      const current = String(item.el.getAttribute(item.attr) || '').trim();
      if (current !== source) continue;
      item.el.setAttribute(item.attr, translated);
      setCachedTranslation(source, translated);
    }
  }
}

export function applyCachedTranslations(root) {
  const collected = collectAllTargets(root, { onlyCjk: true });
  let hit = 0;
  for (let i = 0; i < collected.items.length; i++) {
    const item = collected.items[i];
    const cached = sourceToTranslated.get(item.text);
    if (!cached) continue;
    if (item.type === 'text') {
      const full = item.node.data || '';
      const lead = full.match(/^\s*/)[0];
      const trail = full.match(/\s*$/)[0];
      item.node.data = lead + cached + trail;
    } else if (item.type === 'attr') {
      item.el.setAttribute(item.attr, cached);
    }
    translatedToSource.set(cached, item.text);
    hit += 1;
  }
  return hit;
}

export function restoreSourceTexts(root) {
  const mount = root || document.getElementById('app') || document.body;
  function walk(node) {
    if (!node) return;
    if (node.nodeType === 1) {
      if (SKIP_TAGS.has(node.nodeName)) return;
      for (let i = 0; i < ATTR_NAMES.length; i++) {
        const attr = ATTR_NAMES[i];
        if (!node.hasAttribute || !node.hasAttribute(attr)) continue;
        const text = String(node.getAttribute(attr) || '').trim();
        const source = translatedToSource.get(text);
        if (source) node.setAttribute(attr, source);
      }
    }
    if (node.nodeType === 3) {
      const text = (node.data || '').trim();
      const source = translatedToSource.get(text);
      if (source) {
        const full = node.data || '';
        const lead = full.match(/^\s*/)[0];
        const trail = full.match(/\s*$/)[0];
        node.data = lead + source + trail;
      }
      return;
    }
    let child = node.firstChild;
    while (child) {
      walk(child);
      child = child.nextSibling;
    }
  }
  walk(mount);
}

export function debugCollectCount() {
  const all = collectAllTargets(null, { onlyCjk: false });
  const cjk = collectAllTargets(null, { onlyCjk: true });
  return { all: all.texts.length, cjk: cjk.texts.length, sample: cjk.texts.slice(0, 5) };
}
